import { useParams } from "react-router-dom";
import { Card, Group, SimpleGrid, Stack, Tabs, Text } from "@mantine/core";
import { PageHeader } from "@/components/PageHeader";
import { DetailGrid } from "@/components/DetailGrid";
import { FieldRow } from "@/components/DetailGrid";
import { JsonExpander } from "@/components/JsonExpander";
import { RelatedList } from "@/components/RelatedList";
import { Money } from "@/components/Money";
import { Mono } from "@/components/Mono";
import { StatusBadge } from "@/components/StatusBadge";
import { AdminActionButton } from "@/components/AdminActionButton";
import { OwnerKeyBackupCard } from "@/components/OwnerKeyBackupCard";
import { GenerationBadge } from "@/components/GenerationBadge";
import { SessionKeyPermissionsCard } from "@/components/SessionKeyPermissionsCard";
import { WalletMigrationsCard } from "@/components/WalletMigrationsCard";
import { PreviousWalletsCard } from "@/components/PreviousWalletsCard";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { useFirestoreDoc, useFirestoreQuery } from "@/firestore/hooks";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  SessionKeyDoc,
  WalletAvailabilityDoc,
  WalletDoc,
  WalletKeyInventoryDoc,
  WithDocId,
} from "@/firestore/types";
import { entityListPath } from "@/routes/paths";
import type { RelatedSpec } from "@/entities/types";
import {
  AUTONOMOUS_KEY_TYPES,
  ensureAutonomousKey,
  forceReprovisionAutonomousKey,
  provisionWallet,
} from "@/api/actions";
import { walletConfig } from "@/entities/configs/identity";

/** Tabs of the wallet's owned entities, each a live FK query (no server joins). */
function tabsFor(walletId: string): { value: string; label: string; spec: RelatedSpec }[] {
  const acct = (
    title: string,
    entity: RelatedSpec["entity"],
    collection: RelatedSpec["collection"],
    fkField: string,
    orderBy?: RelatedSpec["orderBy"],
  ): RelatedSpec => ({ title, entity, collection, fkField, value: walletId, orderBy });

  return [
    { value: "cards", label: "Cards", spec: acct("Cards", "card", COLLECTIONS.cards, "account", [["createdAt", "desc"]]) },
    { value: "payments", label: "Payments", spec: acct("Payments", "payment", COLLECTIONS.payments, "account", [["approvedAt", "desc"]]) },
    { value: "cardRefunds", label: "Card refunds", spec: acct("Card refunds (originating wallet)", "cardRefund", COLLECTIONS.cardRefunds, "account", [["createdAt", "asc"]]) },
    { value: "receivedCardRefunds", label: "Received refunds", spec: acct("Refunds received by this wallet", "cardRefund", COLLECTIONS.cardRefunds, "fulfillmentWallet", [["createdAt", "asc"]]) },
    { value: "sessionKeys", label: "Session Keys", spec: acct("Session Keys", "sessionKey", COLLECTIONS.sessionKeys, "account", [["createdAt", "desc"]]) },
    { value: "holds", label: "Holds", spec: acct("Holds", "hold", COLLECTIONS.holds, "wallet", [["createdAt", "desc"]]) },
    { value: "settlements", label: "Settlement Obligations", spec: acct("Settlement obligations", "settlementObligation", COLLECTIONS.settlementObligations, "walletId", [["createdAt", "desc"]]) },
    { value: "executions", label: "Settlement Exec", spec: acct("Settlement executions", "settlement", COLLECTIONS.settlements, "walletId", [["createdAt", "desc"]]) },
    { value: "saos", label: "Smart-Account Ops", spec: acct("Smart-account operations", "smartAccountOperation", COLLECTIONS.smartAccountOperations, "account", [["createdAt", "desc"]]) },
    { value: "ownerOps", label: "Owner Ops", spec: acct("Owner operations", "ownerOperation", COLLECTIONS.ownerOperations, "account", [["createdAt", "desc"]]) },
    { value: "ledger", label: "Ledger", spec: acct("Ledger entries", "ledgerEntry", COLLECTIONS.ledgerEntries, "account", [["createdAt", "desc"]]) },
    { value: "transactions", label: "Transactions", spec: acct("Transactions", "walletTransaction", COLLECTIONS.walletTransactions, "account", [["createdAt", "desc"]]) },
    { value: "deposits", label: "Deposits", spec: acct("Deposits", "deposit", COLLECTIONS.deposits, "account", [["createdAt", "desc"]]) },
    { value: "withdrawals", label: "Withdrawals", spec: acct("Withdrawals", "withdrawal", COLLECTIONS.withdrawals, "account", [["createdAt", "desc"]]) },
    { value: "virtualAccounts", label: "Virtual Accounts", spec: acct("Virtual accounts", "virtualAccount", COLLECTIONS.virtualAccounts, "account", [["createdAt", "desc"]]) },
    { value: "paymentAccounts", label: "Payment Accounts", spec: acct("Payment accounts", "paymentAccount", COLLECTIONS.paymentAccounts, "account", [["createdAt", "desc"]]) },
    // Keyed on `wallet`, not `account` — the wallet-domain sagas use the domain's own vocabulary.
    { value: "forwarderSweeps", label: "Forwarder Sweeps", spec: acct("Forwarder sweeps", "forwarderSweep", COLLECTIONS.forwarderSweeps, "wallet", [["createdAt", "desc"]]) },
    { value: "offrampBurns", label: "Off-Ramp Burns", spec: acct("Off-ramp burns", "offrampBurn", COLLECTIONS.offrampBurns, "wallet", [["createdAt", "desc"]]) },
    { value: "depositAddresses", label: "Deposit Addresses", spec: acct("Deposit addresses", "depositAddress", COLLECTIONS.depositAddresses, "walletId", [["createdAt", "desc"]]) },
    { value: "stablecoinReceiveAddresses", label: "USDT Receive Addresses", spec: acct("USDT receive addresses", "stablecoinReceiveAddress", COLLECTIONS.stablecoinReceiveAddresses, "walletId", [["createdAt", "desc"]]) },
    { value: "crossChainDeposits", label: "X-Chain Deposits", spec: acct("Cross-chain deposits", "crossChainDeposit", COLLECTIONS.crossChainDeposits, "walletId", [["createdAt", "desc"]]) },
    { value: "crossChainSends", label: "X-Chain Sends", spec: acct("Cross-chain sends", "crossChainSend", COLLECTIONS.crossChainSends, "walletId", [["createdAt", "desc"]]) },
    { value: "earnPositions", label: "Earn Positions", spec: acct("Earn positions", "earnPosition", COLLECTIONS.earnPositions, "wallet", [["updatedAt", "desc"]]) },
    { value: "earnOperations", label: "Earn Ops", spec: acct("Earn operations", "earnOperation", COLLECTIONS.earnOperations, "wallet", [["createdAt", "desc"]]) },
  ];
}

function BalancesCard({ walletId }: { walletId: string }) {
  const { data: avail, loading } = useFirestoreDoc<WalletAvailabilityDoc>(
    COLLECTIONS.walletAvailability,
    walletId,
  );
  if (loading) return <LoadingState label="Loading balances…" />;
  if (!avail) {
    return (
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} mb="xs">
          Balances
        </Text>
        <EmptyState title="No availability snapshot" description="walletAvailability not computed yet." />
      </Card>
    );
  }
  return (
    <Card withBorder radius="md" padding="lg">
      <Text fw={600} mb="md">
        Balances & availability
      </Text>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="md">
        <FieldRow label="Spendable" value={<Money micro={avail.spendableMicroUsdc} fw={700} />} />
        <FieldRow label="Main balance" value={<Money micro={avail.mainBalanceMicroUsdc} />} />
        <FieldRow label="Reserve balance" value={<Money micro={avail.reserveBalanceMicroUsdc} />} />
        <FieldRow label="Held on Main" value={<Money micro={avail.heldOnMainMicroUsdc} />} />
        <FieldRow label="Active card auths" value={<Money micro={avail.activeCardAuthsMicroUsdc} />} />
        <FieldRow label="Active user transfers" value={<Money micro={avail.activeUserTransfersMicroUsdc} />} />
        <FieldRow label="Active announcements" value={<Money micro={avail.activeAnnouncementsMicroUsdc} />} />
      </SimpleGrid>
    </Card>
  );
}

/** Mobile-equivalent grant cascade: an enabled key wins over a stale pending/revoked row. */
function autonomousKeyStatus(
  keys: SessionKeyDoc[],
  type: string,
): "enabled" | "pending" | "revoked" | "missing" {
  const matching = keys.filter((k) => k.type === type);
  if (matching.some((k) => k.state === "enabled")) return "enabled";
  if (matching.some((k) => k.state === "pending")) return "pending";
  if (matching.some((k) => k.state === "revoked")) return "revoked";
  return "missing";
}

/**
 * The autonomous backend keys (reserve-top-up, cctp-settlement, reserve-release) the backend uses
 * to act on the wallet without an owner prompt at action time. Surfaces each key's grant status and the operator
 * controls to (re-)raise its install prompt — the fix for wallets that predate auto-provisioning or
 * whose user dismissed / let the prompt expire.
 */
function AutonomousKeysCard({ walletId }: { walletId: string }) {
  const { data: keys, loading } = useFirestoreQuery<SessionKeyDoc>(COLLECTIONS.sessionKeys, {
    filters: [["account", "==", walletId]],
  });

  return (
    <Card withBorder radius="md" padding="lg">
      <Text fw={600} mb="xs">
        Autonomous backend keys
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        Keys the backend signs with without an owner prompt at action time (Main→Reserve top-up,
        Reserve→Rain settlement, Reserve→Main card refunds). Re-raise the install prompt, or force a
        fresh key if revoked.
      </Text>
      {loading ? (
        <LoadingState label="Loading keys…" />
      ) : (
        <Stack gap="sm">
          {AUTONOMOUS_KEY_TYPES.map((type) => {
            const status = autonomousKeyStatus(keys, type);
            return (
              <Group key={type} justify="space-between" wrap="nowrap">
                <Group gap="sm">
                  <Mono value={type} />
                  <StatusBadge state={status} />
                </Group>
                <Group gap="xs">
                  {status !== "enabled" ? (
                    <AdminActionButton
                      action={ensureAutonomousKey}
                      params={{ walletId, type }}
                      variant="light"
                    />
                  ) : null}
                  {status === "revoked" ? (
                    <AdminActionButton
                      action={forceReprovisionAutonomousKey}
                      params={{ walletId, type }}
                      variant="light"
                    />
                  ) : null}
                </Group>
              </Group>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}

export function WalletThreeSixty() {
  const { id } = useParams<{ id: string }>();
  const { data: wallet, loading, error } = useFirestoreDoc<WalletDoc>(COLLECTIONS.wallets, id);
  // Admin-only summary (wallet-service 0.74.0): the generation when the wallet document predates
  // it, and the legacy-key count the Keys panel cross-checks. Absent until deployed: tolerated.
  const inventory = useFirestoreDoc<WalletKeyInventoryDoc>(COLLECTIONS.walletKeyInventories, id);
  const crumbs = [{ label: "Wallets", to: entityListPath("wallet") }];

  if (loading) {
    return (
      <>
        <PageHeader title="Wallet 360" crumbs={crumbs} />
        <LoadingState />
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title="Wallet 360" crumbs={crumbs} />
        <ErrorState error={error} />
      </>
    );
  }
  if (!wallet) {
    return (
      <>
        <PageHeader title="Wallet 360" crumbs={crumbs} />
        <EmptyState title="Not found" description={`No wallet with id ${id}.`} />
      </>
    );
  }

  const w = wallet as WithDocId<WalletDoc>;
  const tabs = tabsFor(w.id);
  const generation = w.contractGeneration ?? inventory.data?.contractGeneration ?? null;

  return (
    <>
      <PageHeader
        title={
          <Group gap="sm">
            Wallet 360
            <StatusBadge state={w.state} />
            <GenerationBadge generation={generation} size="md" />
          </Group>
        }
        subtitle={<Mono value={w.id} />}
        crumbs={crumbs}
        actions={
          w.state === "pendingAddress" ? (
            <AdminActionButton action={provisionWallet} params={{ ownerId: w.owner }} />
          ) : null
        }
      />

      <Stack gap="lg">
        <Card withBorder radius="md" padding="lg">
          <DetailGrid fields={walletConfig.detailFields(w)} />
        </Card>

        <BalancesCard walletId={w.id} />

        <AutonomousKeysCard walletId={w.id} />

        <SessionKeyPermissionsCard walletId={w.id} inventory={inventory} />

        <WalletMigrationsCard wallet={w} generation={generation} />

        <PreviousWalletsCard wallet={w} />

        <OwnerKeyBackupCard wallet={w} />

        <Card withBorder radius="md" padding="md">
          <Tabs defaultValue={tabs[0].value} keepMounted={false}>
            <Tabs.List>
              {tabs.map((t) => (
                <Tabs.Tab key={t.value} value={t.value}>
                  {t.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {tabs.map((t) => (
              <Tabs.Panel key={t.value} value={t.value} pt="md">
                <RelatedList spec={t.spec} />
              </Tabs.Panel>
            ))}
          </Tabs>
        </Card>

        <Card withBorder radius="md" padding="md">
          <Text fw={600} mb="xs">
            Raw document
          </Text>
          <JsonExpander value={w} />
        </Card>
      </Stack>
    </>
  );
}
